/**
 * Front-end model representing a user. See back-end complement for field descriptions.
 *
 * The following Field model attribute sets are located under the static `fields` attribute.
 * * Username
 * * Name
 * * PrimaryName
 * * SecondaryName
 * * Address
 * * PhoneNumber
 * * EmailAddress
 * * Status
 * * OfficeNumber
 * * OfficePhone
 * * Extension
 * * Location
 * @module app/models/User
 */
import Backbone from "backbone";
import $ from "jquery";
import _ from "underscore";
import PermissionUtil from "@HBKEngineering/permission-util";
import RequestUtils from "app/utils/RequestUtils";
import ErrorUtils from "app/utils/ErrorUtils";
import FieldUtils from "app/utils/FieldUtils";
import auth from "app/auth";
import urlService from "app/UrlService";
import messenger from "app/Messenger";
import sessionConfig from "app/session-config";

var User = Backbone.Model.extend(
  {
    initialize: function (attributes, options) {
      Backbone.Model.prototype.initialize.apply(this, arguments);

      this.options = _.defaults({}, options);
    },

    urlRoot: function () {
      return urlService.apiurl("user", true);
    },
    defaults: {
      Username: "",
      PrimaryName: "",
      SecondaryName: "",
      EmailAddress: "",
      Address: "",
      PhoneNumber: "",
      Extension: "",
      Location: "",
      OfficeNumber: "",
      Permissions: [],
    },

    /**
     * Gets a value indicating whether or not this user is considered to be an administrative user.
     * @return {Boolean} True if administrative, otherwise false.
     */
    isAdmin: function () {
      return this.hasAnyRole(["super", "admin"]);
    },

    /**
     * Gets the list of roles (groups) in which this user resides.
     * @return {Promise} A promise object.
     * @async
     */
    getRoles: function () {
      var deferred = new $.Deferred();

      RequestUtils.get(this.url() + "/roles")
        .done(function (data) {
          deferred.resolve(data);
        })
        .fail(function (xhr) {
          deferred.reject(ErrorUtils.normalizeError({}, xhr));
        });

      return deferred.promise();
    },

    hasRole: function (role) {
      if (!_.isArray(role)) {
        role = [role];
      }

      var groups = this.get("Permissions") || [];

      return _.every(role, function (name) {
        return _.contains(groups, name);
      });
    },

    hasAnyRole: function (role) {
      if (!_.isArray(role)) {
        role = [role];
      }

      var groups = this.get("Permissions") || [];

      return _.some(role, function (name) {
        return _.contains(groups, name);
      });
    },

    //determine if the current user can perform the specified action on the given type
    //returns true if they can, otherwise false.
    can: function (action, type) {
      return (
        this.hasRole("admin") ||
        PermissionUtil.hasAnyAction(this.get("permissionSet"), type, action)
      );
    },

    /**
     * Validates the field values.
     * @param  {Function} cb Function expecting two arguments. The first is an error, if any, and the second is a mapping from field name to field error. If no validation errors occurred, this will be an empty object or undefined.
     * @async
     */
    validate: function (cb) {
      if (_.isFunction(cb)) {
        return RequestUtils.post(
          urlService.apiurl("/user/" + this.id + "/validate"),
          this.attributes
        )
          .done(function (data) {
            cb(null, data);
          })
          .fail(function (xhr, data) {
            cb(data);
          });
      } else {
        //validate called by backbone - don't worry about this case for now
      }
    },

    /**
     * Gets the full name of the user.
     * @return {String} The full name of the user.
     */
    fullName: function () {
      var name = "Unnamed Employee",
        firstName = this.get("PrimaryName"),
        lastName = this.get("SecondaryName");

      if (firstName) {
        name = firstName;

        if (lastName) {
          name = name + " " + lastName;
        }
      } else if (lastName) {
        name = lastName;
      }

      return name;
    },

    /**
     * Send an account creation notification email to the user.
     * @return {Promise} A promise object.
     * @async
     */
    sendCreationNotification: function () {
      var self = this;

      return RequestUtils.post(
        sessionConfig.getBaseUrl() + "/password/change",
        {
          token: auth.token,
          reason: "creation",
          identifier: this.id,
        }
      ).then(function (data) {
        self.set("creationEmailSent", new Date());

        return data;
      });
    },

    /**
     * Adds a user to the given group.
     * @param {Group} group The group model to which to add a user.
     * @return {void|PromisesAPlus.Thenable<void>|*} A promise which resolves when the user has been added successfully and rejects when an error occurs. Errors and messages are handled within this function.
     */
    addToGroup: function (group) {
      return group
        .addUser(this.model.id)
        .done(function () {
          messenger.success('User added to group "' + group.get("name") + '".');
        })
        .fail(
          ErrorUtils.ajaxHandler({
            activity: "adding user to group",
          })
        );
    },

    /**
     * Removes a user from the given group.
     * @param {Group} group The group model from which to remove a user.
     * @return {void|PromisesAPlus.Thenable<void>|*} A promise which resolves when the user has been added successfully and rejects when an error occurs. Errors and messages are handled within this function.
     */
    removeFromGroup: function (group) {
      return group
        .removeUser(this.model.id)
        .done(function () {
          messenger.success(
            'User removed from group "' + group.get("name") + '".'
          );
        })
        .fail(
          ErrorUtils.ajaxHandler({
            activity: "removing user from group",
          })
        );
    },

    /**
     * Performs the necessary API calls to update the user's password.
     * @param {String} oldPassword The old password. The value provided when `isAdmin` is true is ignored.
     * @param {String} newPassword The new password
     * @param {Boolean} [isAdmin] True to indicate that the admin flow should be used for updating passwords. Otherwise the normal flow will be used.
     * @return {void|PromisesAPlus.Thenable<void>|*} A promise which resolves when the user has been updated successfully and rejects when an error occurs. Errors and messages are not handled within this function.
     */
    updatePassword: function (oldPassword, newPassword, isAdmin) {
      var deferred = new $.Deferred();
      var reason = "update";
      var activity = "updating the password";

      if (isAdmin) {
        reason = "admin";
        oldPassword = undefined;
      }

      var requestData = {
        token: auth.token,
        reason: reason,
        identifier: this.id,
      };
      var error = function (xhr, textStatus, errorThrown) {
        deferred.reject(
          ErrorUtils.normalizeError(
            {
              textStatus: textStatus,
              errorThrown: errorThrown,
              activity: activity,
            },
            xhr
          )
        );
      };

      $.ajax({
        url: sessionConfig.getBaseUrl() + "/password/changeRequest",
        method: "POST",
        data: JSON.stringify(requestData),
        dataType: "json",
        contentType: "application/json",
        processData: false,
        success: function (data) {
          var token = data.token;

          requestData = {
            token: token,
            reason: reason,
            password: oldPassword,
            new_password: newPassword,
          };

          $.ajax({
            url: sessionConfig.getBaseUrl() + "/password/update",
            method: "POST",
            data: JSON.stringify(requestData),
            dataType: "json",
            contentType: "application/json",
            processData: false,
            success: function () {
              deferred.resolve();
            },
            error: error,
          });
        },
        error: error,
      });

      return deferred.promise();
    },

    toJSON: function () {
      var json = Backbone.Model.prototype.toJSON.apply(this, arguments);

      delete json.groups;

      return json;
    },
  },
  {
    fields: {
      Username: FieldUtils.fieldPrototype("Username", "Username", "text", {
        filterable: true,
        sortable: true,
        required: "required",
      }),
      Name: FieldUtils.fieldPrototype("id", "Name", "link", {
        viewOptions: {
          linkField: "fullName",
          linkUrl: "user",
        },
        editable: false,
        filterable: "fullName",
        sortable: "fullName",
      }),
      name: FieldUtils.fieldPrototype("fullName", "Name", "link", {
        editable: false,
        filterable: true,
        sortable: true,
      }),
      PrimaryName: FieldUtils.fieldPrototype(
        "PrimaryName",
        "First Name",
        "text",
        {
          filterable: true,
          sortable: true,
          required: "required",
        }
      ),
      SecondaryName: FieldUtils.fieldPrototype(
        "SecondaryName",
        "Last Name",
        "text",
        {
          filterable: true,
          sortable: true,
          required: "required",
        }
      ),
      EmailAddress: FieldUtils.fieldPrototype(
        "EmailAddress",
        "Email Address",
        "email",
        {
          filterable: true,
          sortable: true,
          required: "required",
        }
      ),
      Address: FieldUtils.fieldPrototype("Address", "Address", "text"),
      PhoneNumber: FieldUtils.fieldPrototype(
        "PhoneNumber",
        "Mobile Phone",
        "phone"
      ),
      OfficeNumber: FieldUtils.fieldPrototype(
        "OfficeNumber",
        "Office Number",
        "phone"
      ),
      Extension: FieldUtils.fieldPrototype("Extension", "Extension", "text"),
      PasswordActive: FieldUtils.fieldPrototype(
        "hasPassword",
        "Has Password",
        "checkbox",
        {
          editable: false,
          sortable: true,
        }
      ),
      lastLogin: FieldUtils.fieldPrototype("lastLogin", "Last Login", "date", {
        sortable: true,
        viewOptions: {
          type: "fromnow",
        },
      }),
    },
  }
);

export default User;



//////////////////
// WEBPACK FOOTER
// ./public/js/app/models/User.js
// module id = 65
// module chunks = 1